import { useCallback, useMemo, useState } from "react";
import useSWR from "swr";
import { Button, Skeleton, Text } from "@mantine/core";
import DataWrapper from "../../../../common/components/DataWrapper/DataWrapper";
import WalletTransactionToolbar from "../../components/WalletTransactionToolbar/WalletTransactionToolbar";
import { matchesWalletFilters } from "../../utils/transaction";
import { fetcher } from "../../../../services/fetcher";
import { errorNotifications } from "../../../../utils/handleNotifications";
import classes from "./Transaction.module.css";

const endpoint = "asc-wallet/master_wallet_transactions/";
const BULK_PAGE_SIZE = 500;

const WalletTransaction = ({ toolbarRightContent }) => {
  const [paymentModeFilter, setPaymentModeFilter] = useState("");
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("");

  const hasFilters = Boolean(paymentModeFilter || transactionTypeFilter);

  const { data: bulkData, isLoading: bulkLoading } = useSWR(
    hasFilters ? `${endpoint}?page_size=${BULK_PAGE_SIZE}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const filteredRows = useMemo(() => {
    if (!hasFilters) return [];
    const results = Array.isArray(bulkData?.results) ? bulkData.results : [];
    return results.filter((row) =>
      matchesWalletFilters(row, {
        paymentMode: paymentModeFilter,
        transactionType: transactionTypeFilter,
      })
    );
  }, [bulkData, hasFilters, paymentModeFilter, transactionTypeFilter]);

  const handleDownloadScreenshot = useCallback((row) => {
    try {
      const downloadUrl = row?.screenshot;

      if (!downloadUrl) {
        errorNotifications("Screenshot is not available.");
        return;
      }

      const resolvedUrl = new URL(
        downloadUrl,
        import.meta.env.VITE_API_ENDPOINT || window.location.origin
      ).href;

      const link = document.createElement("a");
      link.href = resolvedUrl;
      link.download = "Screenshot";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      errorNotifications("Error downloading screenshot.", error);
    }
  }, []);

  const config = useMemo(
    () => ({
      toolbarPy: "md",
      toolbarLeftGap: "sm",
      toolbarRightGap: "sm",
      toolbarRowClassName: classes.toolbarRow,
      toolbarNoWrap: true,
      searchInputWidth: 176,
      searchInputHeight: 44,
      tableMt: "sm",
      horizontalSpacing: 0,
      verticalSpacing: 0,
      tableStyles: {
        th: {
          height: 44,
          padding: "0 12px",
          borderBottom: "1px solid #D6E4EF",
          verticalAlign: "middle",
        },
        td: {
          height: 44,
          padding: "0 12px",
          verticalAlign: "middle",
        },
      },
      fields: [
        { key: "amount", label: "Amount" },
        { key: "transaction_type", label: "Type" },
        { key: "transaction_number", label: "Number" },
        { key: "payment_mode", label: "Mode" },
        { key: "transaction_date", label: "Date" },
        { key: "wallet.full_name", label: "Name" },
        {
          key: "screenshot",
          label: "Screenshot",
          render: (row) =>
            row?.screenshot ? (
              <Button
                size="xs"
                variant="default"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownloadScreenshot(row);
                }}
              >
                Download
              </Button>
            ) : (
              <Text c="dimmed" size="sm">
                --
              </Text>
            ),
        },
        { key: "created_by", label: "Created By" },
        { key: "description", label: "Description" },
        { key: "status", label: "Status", type: "status" },
      ],
    }),
    [handleDownloadScreenshot]
  );

  const toolbar = (
    <WalletTransactionToolbar
      paymentModeFilter={paymentModeFilter}
      onPaymentModeChange={setPaymentModeFilter}
      transactionTypeFilter={transactionTypeFilter}
      onTransactionTypeChange={setTransactionTypeFilter}
    />
  );

  if (hasFilters && bulkLoading) {
    return (
      <>
        <Skeleton height={52} mb="sm" radius="md" />
        <Skeleton height={360} radius="md" />
      </>
    );
  }

  return (
    <DataWrapper
      key={hasFilters ? "filtered" : "paged"}
      {...(hasFilters ? { rows: filteredRows } : { endpoint })}
      config={config}
      swrConfig={{ revalidateOnFocus: false }}
      toolbarLeftContent={toolbar}
      toolbarRightContent={toolbarRightContent}
    />
  );
};

export default WalletTransaction;
